import { Slot } from '@radix-ui/react-slot';
import { Link, LinkProps } from '@tanstack/react-router';
import { type VariantProps, cva } from 'class-variance-authority';
import { Loader2Icon } from 'lucide-react';
import * as React from 'react';
import { LegacyRef } from 'react';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring data-disabled:opacity-50 data-disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-primary/80 active:bg-primary/70 data-disabled:hover:bg-primary',
        configured:
          'border border-driver-brand-blue bg-white text-gray-900 hover:bg-gray-50 hover:border-driver-brand-blue active:bg-gray-100 active:border-driver-brand-blue data-disabled:hover:bg-white data-disabled:hover:border-driver-brand-blue',
        neonBlue:
          'bg-neon-blue text-primary-foreground hover:bg-neon-blue/80 active:bg-neon-blue/70 data-disabled:hover:bg-neon-blue',
        driverBrandBlue:
          'bg-driver-brand-blue text-primary-foreground hover:bg-driver-brand-blue/80 active:bg-driver-brand-blue/70 data-disabled:hover:bg-driver-brand-blue',
        driverBrandLightBlue:
          'bg-driver-brand-light-blue text-primary-foreground hover:bg-driver-brand-light-blue/80 active:bg-driver-brand-light-blue/70 data-disabled:hover:bg-driver-brand-light-blue',
        porsche: 'bg-porsche hover:bg-porsche/80 active:bg-porsche/70 data-disabled:hover:bg-porsche',
        outline:
          'border border-input hover:bg-accent hover:text-accent-foreground active:bg-accent-emphasis data-disabled:hover:bg-[unset] data-disabled:hover:text-[inherit]',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary-hover active:bg-secondary-emphasis data-disabled:hover:bg-secondary',
        warning:
          'bg-warning text-primary-foreground hover:bg-warning/80 active:bg-warning/70 data-disabled:hover:bg-warning',
        ghost:
          'hover:bg-accent hover:text-accent-foreground active:bg-accent-emphasis text-accent-foreground data-disabled:hover:bg-[unset] data-disabled:hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline data-disabled:hover:no-underline',
        linkBlue: 'text-link underline-offset-4 hover:text-link-hover data-disabled:hover:text-link',
        // TODO: Fix AI button CSS - low priority
        // ai: "relative bg-primary text-primary-foreground disabled:hover:bg-primary hover:bg-transparent rounded-md border-gradient",
        ai: 'bg-primary text-primary-foreground hover:bg-primary/80 active:bg-primary/70 data-disabled:hover:bg-primary',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive-emphasis active:bg-destructive-emphasis/80 data-disabled:hover:bg-destructive',
        destructiveGhost:
          'text-destructive hover:bg-destructive/10 active:bg-destructive/20 data-disabled:hover:bg-[unset]',
        destructiveGhostLowKey: 'hover:bg-destructive/10 active:bg-destructive/20 data-disabled:hover:bg-[unset]',
        destructiveOutline:
          'border border-destructive/50 text-destructive hover:bg-destructive/10 active:bg-destructive/20 data-disabled:hover:bg-[unset]',
        white:
          'bg-white text-foreground shadow-[0_0_0_0.5px_#D8D8D8,0_2px_4px_0_rgba(0,0,0,0.02),0_1px_2px_0_rgba(0,0,0,0.05)] hover:bg-gray-50 active:bg-gray-100 data-disabled:hover:bg-white',
      },
      size: {
        default: 'h-9 px-3 py-2',
        xs: 'h-6 rounded-md px-2 text-xs',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9',
        iconSm: 'h-6 w-6',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

const iconSizeClasses = {
  default: 'h-4 w-4',
  xs: 'h-3.5 w-3.5',
  sm: 'h-3.5 w-3.5',
  lg: 'h-5 w-5',
  icon: 'h-4 w-4',
  iconSm: 'h-3 w-3',
};

const iconMarginClasses = {
  leading: {
    default: '-ml-0.5 mr-2',
    xs: '-ml-0.5 mr-1',
    sm: '-ml-0.5 mr-1',
    lg: '-ml-1 mr-2.5',
    icon: '',
    iconSm: '',
  },
  trailing: {
    default: 'ml-2 -mr-0.5',
    xs: 'ml-1 -mr-0.5',
    sm: 'ml-1 -mr-0.5',
    lg: 'ml-2.5 -mr-1',
    icon: '',
    iconSm: '',
  },
};

export type ButtonIcon = React.ForwardRefExoticComponent<
  React.PropsWithoutRef<React.SVGProps<SVGSVGElement>> & {
    title?: string;
    titleId?: string;
  } & React.RefAttributes<SVGSVGElement>
>;

export interface BaseLinkProps {
  leadingIcon?: ButtonIcon;
  trailingIcon?: ButtonIcon;
  leadingIconClassName?: string;
  trailingIconClassName?: string;
  children?: React.ReactNode;
  loading?: boolean;
}

type IconOnlyA11yProps =
  | {
      iconOnly: true;
      'aria-label': string;
    }
  | {
      iconOnly: boolean;
      'aria-label': string;
    }
  | {
      iconOnly?: false;
      'aria-label'?: string;
    };

export type ButtonProps = BaseLinkProps &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> &
  VariantProps<typeof buttonVariants> &
  IconOnlyA11yProps & {
    asChild?: boolean;
  };

export type ButtonLinkProps = BaseLinkProps &
  Omit<React.ButtonHTMLAttributes<HTMLAnchorElement>, 'children'> &
  Omit<LinkProps, 'children'> &
  VariantProps<typeof buttonVariants> &
  IconOnlyA11yProps;

const Button = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps | ButtonLinkProps>(
  (
    {
      className,
      variant,
      size,
      leadingIcon: LeadingIcon,
      trailingIcon: TrailingIcon,
      leadingIconClassName,
      trailingIconClassName,
      iconOnly = false,
      type,
      children,
      loading,
      ...props
    },
    ref
  ) => {
    const IconComponent = iconOnly ? LeadingIcon || TrailingIcon : null;

    const isDisabled = props.disabled;

    const elemClassName = cn(buttonVariants({ variant, size, className }));

    const buttonContent = loading ? (
      <Loader2Icon className={cn('animate-spin', iconSizeClasses[size || 'default'])} />
    ) : iconOnly && IconComponent ? (
      <IconComponent className={cn(iconSizeClasses[size || 'default'])} />
    ) : (
      <>
        {LeadingIcon && (
          <span
            className={cn(
              iconSizeClasses[size || 'default'],
              iconMarginClasses.leading[size || 'default'],
              leadingIconClassName,
              // Size the SVG to the box: lucide icons carry width/height="24"
              // attributes and would otherwise overflow the span (heroicons
              // have no intrinsic size and already fill it).
              'shrink-0 [&>svg]:size-full'
            )}
          >
            <LeadingIcon />
          </span>
        )}
        {children}
        {TrailingIcon && (
          <span
            className={cn(
              iconSizeClasses[size || 'default'],
              iconMarginClasses.trailing[size || 'default'],
              trailingIconClassName,
              'shrink-0 [&>svg]:size-full'
            )}
          >
            <TrailingIcon />
          </span>
        )}
      </>
    );

    const isLinkProps = (props: any): props is ButtonLinkProps => Boolean(props.to) || Boolean(props.href);

    if (isLinkProps(props)) {
      if (props.href) {
        return (
          <a
            {...props}
            className={elemClassName}
            data-disabled={isDisabled ? true : undefined}
            ref={ref as LegacyRef<HTMLAnchorElement>}
          >
            {buttonContent}
          </a>
        );
      } else {
        return (
          <Link
            {...props}
            className={elemClassName}
            data-disabled={isDisabled ? true : undefined}
            ref={ref as LegacyRef<HTMLAnchorElement>}
          >
            {buttonContent}
          </Link>
        );
      }
    } else {
      const { asChild, ...rest } = props as ButtonProps;
      const Comp = asChild ? Slot : 'button';

      return (
        <Comp
          className={elemClassName}
          data-disabled={isDisabled ? true : undefined}
          type={type || 'button'}
          ref={ref as LegacyRef<HTMLButtonElement>}
          {...rest}
        >
          {buttonContent}
        </Comp>
      );
    }
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
